import Modal from './Modal'

interface HowToPlayProps {
  onClose: () => void
}

// A little slice of the real hex grid for the jump demo. Axial (q,r) → pixels
// exactly like the board (staggered rows). The marble starts at the bottom and
// chain-jumps twice up the same hex diagonal — a natural forward double-jump.
const HEX = 26
const OX = 36
const OY = 20
const cell = (q: number, r: number) => ({
  x: OX + HEX * (q + r / 2),
  y: OY + HEX * 0.866 * r,
})
const HOLES = (
  [
    [0, 0], [0, 1], [0, 2], [0, 3], [0, 4], // the jump lane
    [-1, 1], [-1, 2], [-1, 3], // flanking holes for hex context
    [1, 1], [1, 2], [1, 3],
  ] as const
).map(([q, r]) => cell(q, r))
const OBSTACLE_A = cell(0, 3) // jumped first (moving up)
const OBSTACLE_B = cell(0, 1) // jumped second, same direction (up)
const HOPPER_START = cell(0, 4)

export default function HowToPlay({ onClose }: HowToPlayProps) {
  return (
    <Modal
      title="How to Play"
      onClose={onClose}
      actions={
        <button className="sidebar-primary" type="button" onClick={onClose}>
          Got it
        </button>
      }
    >
      <p className="howto-lead">
        Race your ten marbles across the board and fill the star point opposite your
        home before your opponent fills theirs.
      </p>

      <ol className="howto-list">
        <li>
          <span className="howto-step">Step</span>
          Slide one marble into a touching empty hole in any direction.
        </li>
        <li>
          <span className="howto-step">Jump</span>
          Hop a marble over any single neighbouring marble (yours or your
          opponent's) into the empty hole directly beyond it.
        </li>
        <li>
          <span className="howto-step">Chain</span>
          A jump can continue: keep hopping over marbles in one turn to fly clear
          across the board.
        </li>
        <li>
          <span className="howto-step">Win</span>
          The first player to land all ten marbles in the opposite home triangle
          wins.
        </li>
      </ol>

      <div className="howto-demo" aria-hidden="true">
        {HOLES.map((h, i) => (
          <span key={i} className="howto-hole" style={{ left: h.x - 6, top: h.y - 6 }} />
        ))}
        <span
          className="howto-marble howto-marble-b"
          style={{ left: OBSTACLE_A.x - 11, top: OBSTACLE_A.y - 11 }}
        />
        <span
          className="howto-marble howto-marble-g"
          style={{ left: OBSTACLE_B.x - 11, top: OBSTACLE_B.y - 11 }}
        />
        <span
          className="howto-marble howto-hopper"
          style={{ left: HOPPER_START.x - 11, top: HOPPER_START.y - 11 }}
        />
      </div>
      <p className="howto-demo-cap">Chain jumps clear several marbles in one turn.</p>
    </Modal>
  )
}
